// @ts-nocheck
import { interactivityRegistration } from "./Charta";
import { DomQuery, WithClass } from "./Dom";
import { ConfirmationClass, ConfirmationWindowComponent } from "./WebApp.ConfirmationWindow";
import { FileUploadState } from "./WebApp.File_.Store";
import { application } from "./WebApp.Instance";
import { FileUploadStatusChangedEvent } from "./WebApp.Upload.Center";
import { FileUploadedEvent, UploadArea } from "./WebSite.Page.Component.UploadArea";
import { Labels, WebPageComponent } from "./WebSite.Page.Component";

export class ImageGrid extends WebPageComponent {
  constructor(element) {
    super(element);

    this.images = new DomQuery(this.element).getChild(WithClass("Images"));
    this.uploadArea = null;
  }

  bind() {
    this.uploadArea = this.getAncestor((component) => component instanceof UploadArea);

    if (this.isEditable()) {
      this.labels = new Labels(new DomQuery(this.element).getChild(WithClass("Labels")));

      for (const container of this.images.querySelectorAll(".Image"))
        this.addDeleteControl(container);
    }
  }

  isEditable() {
    return this.uploadArea !== null;
  }

  async addFile(file) {
    this.addImage(file);
  }

  async downloadThumbnail(uuid) {
    let result = null;
    const uri = `${this.uri}/${uuid}/Thumbnail`;

    try {
      const response = await fetch(uri);

      if (response.ok) {
        const blob = await response.blob();
        const file = {
          target: uri,
          name: uuid,
          uuid: uuid,
          state: FileUploadState.Submitted,
          attempts: 0,
          progress: 0,
          problems: [],
          location: uri
        };

        await application.fileStore.add(file);
        await application.pageHandler.persistThumbnail(file, blob);

        result = file;
      }
    } catch (error) {
      console.warn(`Failed to fetch thumbnail for file ${uuid}:`, error);
    }

    return result;
  }

  addImage(file) {
    const image = document.createElement("img");
    image.src = URL.createObjectURL(new Blob([file.thumbnail]));
    image.title = file.name;
    image.dataset.Name = file.uuid ? file.uuid : file.id;

    const container = document.createElement("div");
    container.classList.add("Image");
    container.appendChild(image);

    this.updateImageState(container, file.state);
    this.images.appendChild(container);

    if (this.isEditable())
      this.addDeleteControl(container);
  }

  addDeleteControl(container) {
    const button = document.createElement("button");
    button.type = "button";
    button.classList.add("Delete");
    button.addEventListener(
      "click",
      (event) => {
        this.confirmDelete(container);

        event.stopPropagation();
        event.preventDefault();
      }
    );

    container.appendChild(button);
  }

  confirmDelete(container) {
    const name = container.querySelector("[data--name]")?.dataset.Name;

    if (name) {
      const confirmation = new ConfirmationWindowComponent(
        this.labels.DeleteConfirmation,
        this.labels.Confirm,
        this.labels.Cancel,
        () => this.deleteImage(name),
        ConfirmationClass.Delete
      );

      confirmation.show();
    }
  }

  async deleteImage(id) {
    const file = await this.findFile(id);

    if (file) {
      if (file.location === null)
        await application.fileStore.delete(file.id);
      else
        await application.fileStore.queueDeletion(file, this.uploadArea.URI);
    }
    else if (!this.isNumericIdentifier(id))
      await this.queueServerImageDeletion(id);

    this.removeImage(id);
    this.fireDataChanged();
  }

  findFile(id) {
    return this.isNumericIdentifier(id) ? application.fileStore.get(parseInt(id)) : application.fileStore.getThumbnailByUuid(id);
  }

  isNumericIdentifier(identifier) {
    return /^\d+$/.test(identifier);
  }

  async queueServerImageDeletion(uuid) {
    await application.fileStore.add({
      target: this.uploadArea.URI,
      uuid: uuid,
      name: uuid,
      state: FileUploadState.Aborted,
      location: `${this.uri}/${uuid}/Thumbnail`,
      processed: 0,
      attempts: 0,
      progress: 0,
      problems: []
    });
  }

  removeImage(id) {
    const element = this.images.querySelector(`.Image > [data--name="${id}"]`);

    if (element)
      element.parentElement.remove();
  }

  updateImageState(image, state) {
    image.classList.remove("Successful", "Uploading", "Error");

    if (state === FileUploadState.Submitted)
      image.classList.add("Successful");
    else if (FileUploadState.uploadingStates.has(state))
      image.classList.add("Uploading");
    else
      image.classList.add("Error");
  }

  handleEvent(event) {
    if (event instanceof FileUploadedEvent && event.target === this.parentComponent)
      this.addFile(event.data);
    else if (event instanceof FileUploadStatusChangedEvent) {
      const image = this.images.querySelector(
        `.Image > img[data--name="${event.upload.id}"], .Image > img[data--name="${event.upload.uuid}"]`
      );

      if (image)
        this.updateImageState(image.parentElement, event.upload.state);
    }
    else
      super.handleEvent(event);
  }

  load(value) {
    this.reconcileImages();
  }

  async reconcileImages() {
    let files = [];

    if (this.isEditable())
      files = await application.fileStore.getMatches("target", this.uploadArea.URI);

    this.removeDeletedImages(files);
    this.refreshThumbnails();
    await this.restoreImages(files);
  }

  removeDeletedImages(files) {
    const abortedUuids = new Set(
      files
        .filter(file => file.state === FileUploadState.Aborted && file.uuid)
        .map(file => file.uuid)
    );

    for (const container of this.images.querySelectorAll(".Image")) {
      const element = container.querySelector("[data--name]");

      if (element && abortedUuids.has(element.dataset.Name))
        container.remove();
    }
  }

  refreshThumbnails() {
    for (const container of this.images.querySelectorAll(".Image")) {
      const element = container.querySelector("[data--name]");

      if (element)
        this.refreshThumbnail(element);
    }
  }

  async refreshThumbnail(element) {
    const image = element.tagName === "IMG" ? element : element.querySelector("img");

    if (image) {
      const file = await this.resolveThumbnail(element.dataset.Name);

      if (file && file.thumbnail !== null)
        image.src = URL.createObjectURL(new Blob([file.thumbnail]));
    }
  }

  async resolveThumbnail(id) {
    let result = await application.pageHandler.retrieveThumbnail(id);

    if (!result || result.thumbnail === null)
      result = await this.downloadThumbnail(id);

    return result;
  }

  async restoreImages(files) {
    for (const file of files)
      await this.restoreImage(file);
  }

  async restoreImage(file) {
    if (this.shouldRestore(file)) {
      const fileWithThumbnail = await application.fileStore.getThumbnail(file.id);

      if (fileWithThumbnail)
        this.addImage(fileWithThumbnail);
    }
  }

  shouldRestore(file) {
    return file.state !== FileUploadState.Forbidden
      && file.state !== FileUploadState.Aborted
      && !this.isDisplayed(file);
  }

  isDisplayed(file) {
    return (file.uuid && this.images.querySelector(`.Image > [data--name="${file.uuid}"]`) !== null)
      || this.images.querySelector(`.Image > [data--name="${file.id}"]`) !== null;
  }

  save() {
    return {};
  }

  get name() {
    return this.element.dataset.Name;
  }

  get uri() {
    return this.element.dataset.Uri;
  }
}

interactivityRegistration.register("ImageGrid", (element) => { return new ImageGrid(element) });
